export const encodeProjectDetailsQuery = (data, isProjectSummary = false) => {
    const queryParts = [];
    let sortBy = null;
    let orderBy = null;
    for (const key in data) {
        if (data[key] === "DESC" || data[key] === "ASC") {
            sortBy = key;
            orderBy = data[key];
        }
        else {
            queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`);
        }
    }
    if (sortBy && orderBy) {
        queryParts.push(`sortBy=${encodeURIComponent(sortBy)}&orderBy=${encodeURIComponent(orderBy)}`);
    }
    if (isProjectSummary) {
        queryParts.push("limit=25");
    }
    return queryParts.join("&");
};
export const isEmpty = (arr) => {
    return Array.isArray(arr) && arr.length ? false : true;
};
export const sortByChargingOptions = (chargingOptions, sortByFilter) => {
    if (!Array.isArray(chargingOptions))
        return [];
    if (sortByFilter === "fastest") {
        return [...chargingOptions].sort((optionA, optionB) => {
            const completionTimeA = Number(optionA?.directChargeToMachine?.completionTime ?? 0);
            const completionTimeB = Number(optionB?.directChargeToMachine?.completionTime ?? 0);
            return completionTimeA - completionTimeB;
        });
    }
    if (sortByFilter === "fully charged") {
        return [...chargingOptions].sort((optionA, optionB) => {
            const completionTimeA = Number(optionA?.directChargeToMachine?.completionTime ?? 0);
            const completionTimeB = Number(optionB?.directChargeToMachine?.completionTime ?? 0);
            const socA = Number(optionA?.directChargeToMachine?.machineTargetSoc ?? 0);
            const socB = Number(optionB?.directChargeToMachine?.machineTargetSoc ?? 0);
            if (socA === socB)
                return completionTimeA - completionTimeB;
            return socB - socA;
        });
    }
    if (sortByFilter === "closest ess") {
        return [...chargingOptions].sort((optionA, optionB) => {
            const travelTimeA = Number(optionA?.directChargeToMachine?.totalTravelTime ?? 0);
            const travelTimeB = Number(optionB?.directChargeToMachine?.totalTravelTime ?? 0);
            return travelTimeA - travelTimeB;
        });
    }
    // Unknown filter -> return original array copy
    return [...chargingOptions];
};
/**
 * Gets the timezone abbreviation for a given timezone and optional timestamp.
 *
 * @param projectTimezone - IANA timezone name (e.g., "Europe/Amsterdam") or offset string (e.g., "UTC+01:00")
 * @param timestamp - Optional timestamp to determine DST (if not provided, uses current time)
 * @returns Timezone abbreviation (e.g., "CET", "CEST", "GMT", "BST")
 */
export const getTimezoneAbbr = (projectTimezone, timestamp) => {
    // Handle undefined/null timezone
    if (!projectTimezone) {
        return "UTC";
    }
    // Handle offset string format (e.g., "UTC+01:00") - just return it as-is since it's already an abbreviation
    if (/^UTC[+-]\d{2}:\d{2}$/.test(projectTimezone)) {
        return projectTimezone;
    }
    // Handle IANA timezone names (e.g., "Europe/Amsterdam")
    try {
        // Use provided timestamp or current time to determine the correct abbreviation (DST-aware)
        const dateToUse = timestamp ? new Date(timestamp.endsWith("Z") ? timestamp : `${timestamp}Z`) : new Date();
        // Try short format first (e.g., "GMT+1", "GMT", "EST")
        const shortFormat = dateToUse.toLocaleString("en-US", {
            timeZone: `${projectTimezone}`,
            timeZoneName: "short"
        });
        // Extract timezone part from the formatted string (e.g., "12/18/2025, 12:50:00 AM GMT+1")
        const shortSplit = shortFormat.split(", ");
        if (shortSplit.length > 1) {
            const timePart = shortSplit[1]; // "12:50:00 AM GMT+1"
            const words = timePart.split(" ");
            const tzPart = words[words.length - 1]; // Get last word (timezone)
            if (tzPart) {
                // Convert GMT+offset to proper abbreviations based on timezone
                // Need to distinguish between London GMT+1 (BST) and Amsterdam GMT+1 (CET)
                if (tzPart === "GMT+1") {
                    // Check if it's UK or Central Europe
                    if (projectTimezone.includes("London") || projectTimezone.includes("Dublin")) {
                        return "BST"; // British Summer Time
                    }
                    return "CET"; // Central European Time
                }
                if (tzPart === "GMT+2")
                    return "CEST"; // Central European Summer Time
                if (tzPart === "GMT" || tzPart === "GMT+0")
                    return "GMT"; // Greenwich Mean Time
                if (tzPart === "BST")
                    return "BST"; // British Summer Time (when explicitly provided)
                return tzPart; // Return as-is for other timezones (EST, PST, etc.)
            }
        }
        // Fallback to long format and abbreviate
        const formattedDate = dateToUse.toLocaleString("en-US", {
            timeZone: `${projectTimezone}`,
            timeZoneName: "long"
        });
        const splittimezone = formattedDate.split("AM ");
        const abbr = splittimezone?.length > 1 ? splittimezone[1] : formattedDate.split("PM ")[1];
        // If abbr is still undefined, fallback to UTC
        if (!abbr) {
            return "UTC";
        }
        const abbrShort = abbr
            .split(" ")
            .map((word) => word[0])
            .join("");
        return abbrShort;
    }
    catch (error) {
        // Fallback if timezone is invalid
        return "UTC";
    }
};
// Helper to get CEST time string in your format
export function getLocalTimeInCESTFormat(timezone) {
    // Default to Europe/Amsterdam if timezone is not provided
    const tz = timezone || "Europe/Amsterdam";
    const date = new Date();
    // Convert to specified timezone
    const options = {
        day: "2-digit",
        hour: "2-digit",
        hour12: true,
        minute: "2-digit",
        month: "2-digit",
        timeZone: tz,
        year: "numeric"
    };
    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const formatted = `${get("month")}/${get("day")}/${get("year")}; ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
    return formatted;
}
// Helper: get current hour/minute in specified timezone without round-trip parse
export const getHourMinuteForTimezone = (tz) => {
    const dtf = new Intl.DateTimeFormat("en-CA", {
        day: "2-digit",
        hour: "2-digit",
        hour12: false,
        minute: "2-digit",
        month: "2-digit",
        timeZone: tz,
        year: "numeric"
    });
    const parts = {};
    dtf.formatToParts(new Date()).forEach((p) => {
        if (p.type !== "literal")
            parts[p.type] = p.value;
    });
    return { hour: Number(parts.hour), minute: Number(parts.minute) };
};
// Get placeholder time in 12-hour format (current time + 1 hour)
export const getPlaceholderTime = (timezone) => {
    const tz = timezone || "Europe/Amsterdam";
    const { hour, minute } = getHourMinuteForTimezone(tz);
    // Add 1 hour
    let newHour = hour + 1;
    const newMinute = minute;
    // Handle overflow past 24 hours
    if (newHour >= 24) {
        newHour = newHour - 24;
    }
    // Convert to 12-hour format
    let hour12 = newHour % 12;
    if (hour12 === 0) {
        hour12 = 12; // 0 should be displayed as 12
    }
    // Format as hh:mm
    const formattedHour = String(hour12).padStart(2, "0");
    const formattedMinute = String(newMinute).padStart(2, "0");
    return `${formattedHour}:${formattedMinute}`;
};
// Get default AM/PM based on current time in specified timezone
export const getDefaultTimeUnits = (timezone) => {
    const tz = timezone || "Europe/Amsterdam";
    const dtf = new Intl.DateTimeFormat("en-CA", {
        hour: "2-digit",
        hour12: false,
        timeZone: tz
    });
    const partsArray = dtf.formatToParts(new Date());
    const hourPart = partsArray.find((p) => p.type === "hour");
    const hour = hourPart ? Number(hourPart.value) : 0;
    // Add 1 hour for placeholder logic
    const newHour = (hour + 1) % 24;
    return newHour >= 12 ? "PM" : "AM";
};
